import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Row, Modal, Form } from "react-bootstrap";
import { FormContext } from "../../reusable_components/input_fields/FormContext";
import Element from "../../reusable_components/input_fields/Input";
import { v4 as uuidv4 } from "uuid";
import "react-toastify/dist/ReactToastify.css";
import TemplateDownload from "../../../Reusables/components/TemplateDownload/TemplateDownload";
import alertModalStyles from "../../../Reusables/components/AlertModal/AlertModal.module.css";
import styles from "./UploadSpreadsheetModal.module.css";
import wetSignStyles from "../../engagement_letter/WetSignUploadModal/WetSignUploadModal.module.css";

import { buildSelectOptions, handleChange, handleClose } from "./UploadSpreadsheetModal.utils";
import { populateTemplate } from "./UploadSpreadsheetModal.service";

import {
  SelectOption,
  UploadSpreadsheetModalProps,
} from "./UploadSpreadsheetModal.types";
import SelectInput from "../../../Reusables/components/SelectInput/SelectInput";
import { FieldProps } from "../../reusable_components/input_fields/Input";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";


const UploadSpreadsheetModal: React.FC<UploadSpreadsheetModalProps> = ({
  show,
  onHide,
  refresh,
  options
}) => {
  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [showTransformConfirm, setShowTransformConfirm] = useState(false);
  const [hasApprovedTransformation, setHasApprovedTransformation] = useState(false);

  const [elements, setElements] = useState<FieldProps[]>([
    {
      id: uuidv4(),
      label: null,
      mandatory: false,
      approval: false,
      disabled: false,
      placeholder: "Upload Spreadsheet File",
      type: "file",
      example: null,
      pivot: true,
      pivotreverse: false,
      value: "",
      width: 12,
      fileTypes: ".xlsx,.csv,.xls"
    },
  ]);

  const [selectOptions, setSelectOptions] = useState<SelectOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectOption>({
    id: "",
    label: "",
    value: ""
  });

  
  useEffect(() => {
    const built = buildSelectOptions(options);
    setSelectOptions(built);
  }, [options]);


  const handleSubmit = async (overrideApproved?: boolean) => {
    if (!elements[0].value || !selectedOption?.id) return;

    const isApproved =
    overrideApproved !== undefined
      ? overrideApproved
      : hasApprovedTransformation;

    const result = await populateTemplate(
      elements[0].value,
      selectedOption,
      refresh,
      onHide,
      setFileUploading,
      isApproved
    );

    if (!isApproved && result === "INVALID_SPREADSHEET") {
      setShowTransformConfirm(true);
    }
  };

  useEffect(() => {
    if (!show) {
      setHasApprovedTransformation(false);
      setShowTransformConfirm(false);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={() => handleClose(setFileUploading, onHide)}
      size="lg"
      centered
      keyboard={false}
      dialogClassName={`${alertModalStyles.dialog} ${
        showTransformConfirm ? styles.modalInvisible : ""
      }`}
      contentClassName={alertModalStyles.content}
      backdropClassName={alertModalStyles.backdrop}
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <Modal.Title className={alertModalStyles.title}>
          {"Bulk Client Create"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        {showTransformConfirm && (
          <AlertModal
            show={showTransformConfirm}
            onHide={() => setShowTransformConfirm(false)}
            title="Invalid Spreadsheet Format"
            message="The uploaded spreadsheet does not match the required template. Would you like to transform it to the correct format?"
            type="warning"
            footerButtons={[
              {
                label: "Cancel",
                onClick: () => setShowTransformConfirm(false),
                type: "quinary",
              },
              {
                label: "Continue",
                onClick: async () => {
                  setShowTransformConfirm(false);
                  setHasApprovedTransformation(true);
                  await handleSubmit(true);
                },
                type: "tertiary",
              },
            ]}
          />
        )}

        <Row>
          <SelectInput
            field={{
              id: "selectedField",
              type: "select",
              label: "Select Line of Service",
              value: selectedOption.value,
              options: selectOptions,
              textStyle: { fontSize: 11 },
              validation: [{ type: "required", message: "Required!" }],
              show: true,
              width: 100
            }}
            handleChange={(fieldId: any, val: any) => {
              if (val) setSelectedOption(val);
            }}
            handleBlur={() => {}}
            handleSelect={() => {}}
          />
        </Row>
            
        <div className={styles.templateDownload}>
          <TemplateDownload
            title={"Spreadsheet Template (.xlsx)"}
            endpoint="/clients/template_excel/"
            params={{
              LineOfServiceCode: selectedOption.id.replace("for_", ""),
              LineOfServiceLabel: selectedOption.label,
            }}
            extension={"xlsx"}
            filename={selectedOption.label + "_Bulk_Upload"}
            disabled={!selectedOption?.value}
          />
        </div>

        <FormContext.Provider
          value={{
            handleChange: (field_id, event) =>
              handleChange(elements, setElements, field_id, event),
          }}
        >
          <Form>
            <Row>
              <label className={`${alertModalStyles.message} ${styles.spreadsheetLabel}`}>
                Upload Spreadsheet File
              </label>

              {elements.map((field, i) => (
                <Element key={i} field={field} md={12} />
              ))}
              {elements[0].value && (
                  <div className={wetSignStyles.filePreviewContainer}>
                      <div className={wetSignStyles.filePreview}>
                      <div className={wetSignStyles.fileLeft}>
                          <span className={wetSignStyles.fileIcon}>📄</span>
                          <span className={wetSignStyles.fileName}>{elements[0].value.name}</span>
                      </div>

                      <span
                          className={wetSignStyles.removeIcon}
                          onClick={() =>
                          setElements(prev => [{ ...prev[0], value: "" }])
                          }
                      >
                          ✖
                      </span>
                      </div>
                  </div>
              )}
              <div className={styles.rowLimitWarning}>
                <span className={styles.asterisk}>*</span>
                Maximum allowed row count for the bulk spreadsheet is <strong>200</strong>.
              </div>
            </Row>
          </Form>
        </FormContext.Provider>
      </Modal.Body>
      
      <Modal.Footer className={`${alertModalStyles.footer} ${styles.footer}`}>
          <CustomButton 
              onclick={onHide} 
              content="Cancel" 
              type="quinary"
          />
          <CustomButton
              progress={fileUploading}
              onclick={() => handleSubmit(false)}
              content="Submit"
              disabled={!elements[0].value || !selectedOption?.id}
              type="tertiary"
          />
      </Modal.Footer>
    </Modal>
  );
};

export default UploadSpreadsheetModal;